import type { ProtoEvent } from '../../data/types';
import { bassContour } from '../model';

interface Props {
  events: ProtoEvent[];
  keyToken: string;
  width?: number;
  height?: number;
  labels?: boolean;
  active?: number;
  title?: string;
}

/**
 * A path's written sign: its bass line, drawn by scale step.
 * The dashed line is the tonic, the floor of home.
 */
export function Glyph({ events, keyToken, width = 120, height = 64, labels = false, active = -1, title }: Props) {
  const contour = bassContour(events, keyToken);
  if (!contour.length) return null;
  const steps = contour.map((c) => c.step);
  const lo = Math.min(0, ...steps) - 0.5;
  const hi = Math.max(1, ...steps) + 0.5;
  const padX = 12;
  const padTop = labels ? 16 : 8;
  const padBottom = labels ? 6 : 8;
  const x = (i: number) => (contour.length === 1 ? width / 2 : padX + (i * (width - 2 * padX)) / (contour.length - 1));
  const y = (s: number) => padTop + ((hi - s) / (hi - lo)) * (height - padTop - padBottom);
  const pts = contour.map((c, i) => `${x(i).toFixed(1)},${y(c.step).toFixed(1)}`).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title ?? `Bass line ${contour.map((c) => c.degree).join(', ')}`}>
      <line x1={4} x2={width - 4} y1={y(0)} y2={y(0)} className="s-rest" strokeDasharray="2 4" strokeWidth={1} />
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinejoin="round" strokeLinecap="round" />
      {contour.map((c, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(c.step)} r={i === active ? 5 : 3.4} className={i === active ? 'f-tension' : undefined} fill={i === active ? undefined : 'currentColor'} />
          {labels && (
            <text x={x(i)} y={y(c.step) - 8} textAnchor="middle" className="t-mono">
              {c.degree}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}
